import { Component, EventEmitter, Input, OnInit, OnDestroy, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { EventService } from '../shared/services/event.service';
import { GuestOwnerService } from '../shared/services/guest-owner.service';

@Component({
  selector: 'app-invite-link-sheet',
  standalone: false,
  template: `
    <app-dialog [visible]="true" (visibleChange)="closed.emit()" presentation="sheet">
      <div class="modal-header"><h5>{{ 'notifications.inviteSheetTitle' | translate }}</h5></div>
      <div class="modal-body text-center">
        <h5>{{ matchName }}</h5>
        <p>{{ 'notifications.inviteSheetDescription' | translate }}</p>
        <app-loading-state *ngIf="loading" [compact]="true"></app-loading-state>
        <p *ngIf="error" class="text-danger" role="alert">{{ error }}</p>
        <p *ngIf="copied" role="status">{{ 'guestOwner.share.copied' | translate }}</p>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" (click)="closed.emit()">{{ 'common.close' | translate }}</button>
        <button type="button" class="btn btn-primary" [disabled]="loading || sharing || !url" (click)="share()" data-testid="invite-sheet-share">
          <i [class]="'common.share' | nogoshIcon" aria-hidden="true"></i> {{ 'players.invite' | translate }}
        </button>
      </div>
    </app-dialog>`
})
export class InviteLinkSheetComponent implements OnInit, OnDestroy {
  @Input() eventId: string;
  @Input() matchName: string;
  @Input() match: { nextOccurrenceTimestamp?: Date | string; startDateTime?: string; nextOccurrenceTimestampZone?: string; locationName?: string; playSpace?: { name?: string } };
  @Input() inviteUrl = '';
  @Output() closed = new EventEmitter<void>();
  url = '';
  loading = false;
  sharing = false;
  copied = false;
  error = '';
  private subscription: Subscription;
  constructor(private events: EventService, private translate: TranslateService, private guestOwner: GuestOwnerService) {}
  ngOnInit(): void {
    if (this.inviteUrl) { this.url = this.inviteUrl; return; }
    if (!this.eventId) { this.error = this.translate.instant('players.unableInvite'); return; }
    this.loading = true;
    this.subscription = this.events.ensureInviteLink(this.eventId).subscribe({
      next: invite => {
        const path = invite?.invitePath;
        this.url = path ? this.guestOwner.publicInviteUrl({ invitePath: path } as any) : '';
        this.loading = false;
        if (!this.url) this.error = this.translate.instant('players.unableInvite');
      },
      error: () => { this.loading = false; this.error = this.translate.instant('players.unableInvite'); }
    });
  }
  ngOnDestroy(): void { this.subscription?.unsubscribe(); }
  private shareText(): string {
    const lines = [this.translate.instant('notifications.shareInvite', { matchName: this.matchName })];
    const timestamp = this.match?.nextOccurrenceTimestamp || this.match?.startDateTime;
    const date = timestamp ? new Date(timestamp) : null;
    if (date && !Number.isNaN(date.getTime())) {
      try {
        lines.push('📅 ' + new Intl.DateTimeFormat(this.translate.currentLang?.startsWith('hr') ? 'hr-HR' : 'en-GB', {
          day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
          hour12: false, timeZone: this.match?.nextOccurrenceTimestampZone || undefined,
        }).format(date));
      } catch (_) { /* Invalid legacy timezone metadata must not block sharing. */ }
    }
    const location = this.match?.locationName || this.match?.playSpace?.name;
    if (location?.trim()) lines.push('📍 ' + location.trim());
    return lines.join('\n');
  }
  async share(): Promise<void> {
    if (!this.url || this.sharing) return;
    this.sharing = true;
    this.error = '';
    this.copied = false;
    const text = this.shareText();
    try {
      if (navigator.share) {
        // One text item matches mobile and avoids duplicate URL attachments in share targets.
        await navigator.share({ title: this.matchName, text: `${text}\n${this.url}` });
      } else {
        await navigator.clipboard.writeText(`${text}\n${this.url}`);
        this.copied = true;
      }
    } catch (error) {
      if (error?.name !== 'AbortError') this.error = this.translate.instant('publicProfile.shareError');
    } finally { this.sharing = false; }
  }
}
